import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useToastMessage } from 'hooks';
import useTheme from 'hooks/useTheme';
import AntButton from 'components/AntButton/AntButton';
import MaterialValidationDecisionModal, {
	MANUAL_CORRECTION_MODE,
} from './MaterialValidationDecisionModal';
import MaterialValidationHistory from './MaterialValidationHistory';
import {
	formatMaterialValidationCaseLabel,
	formatMaterialValidationValue,
	getComparisonExpectedLabel,
	getComparisonOutcomeLabel,
} from './materialValidationPresentation';
import {
	getOrderCurrentInventory,
	isManualCorrectionCase,
	isManualCorrectionReadyForReview,
	isManualCorrectionWaiting,
	isValidMaterialEvaluationSha256,
	requiresLiveMaterialEvaluation,
	TECHNICIAN_INVENTORY_RESOLUTION_KEY,
} from './materialValidationContract';

const EVALUATION_STATUS_LABELS = {
	MATCH: 'Coincide',
	MISMATCH: 'Requiere revisión',
	INDETERMINATE: 'No determinable',
};

const INVENTORY_AUDIT_REASON_LABELS = {
	ORIGINAL_WTD_SEAL_MISSING:
		'La evidencia original del inventario no está disponible, por lo que no fue posible calcular los cambios.',
	ORIGINAL_WTD_SEAL_INVALID:
		'La evidencia original del inventario no es válida, por lo que no fue posible calcular los cambios.',
	CURRENT_WTD_SEAL_INVALID:
		'El inventario actual no es válido, por lo que no fue posible calcular los cambios.',
};

const CURRENT_INVENTORY_REASON_LABELS = {
	CURRENT_WTD_SEAL_INVALID:
		'El inventario técnico actual no se puede verificar porque la evidencia vigente no es válida.',
	AMBIGUOUS_SERIALIZED_INVENTORY:
		'El inventario técnico actual no se puede verificar porque existen series ambiguas.',
};

const INVENTORY_VIEW_AUDIT = 'AUDIT';
const INVENTORY_VIEW_CURRENT = 'CURRENT';

const pairedInventoryChangeKey = change => {
	if (change.changeType === 'STATE_CHANGED')
		return `state:${change.seriesId ?? change.series}`;
	if (change.changeType === 'SERIAL_SUBSTITUTED')
		return change.changeGroupId !== undefined && change.changeGroupId !== null
			? `group:${change.changeGroupId}`
			: `item:${change.original?.item?.itemId ?? change.target?.item?.itemId}`;
	return null;
};

const compareInventorySeries = (left, right) => {
	const leftKey = String(left.seriesId ?? left.series ?? '');
	const rightKey = String(right.seriesId ?? right.series ?? '');
	return leftKey.localeCompare(rightKey);
};

const groupPairedInventoryChanges = changes => {
	const changesByIdentity = changes.reduce((byIdentity, change) => {
		const identity = pairedInventoryChangeKey(change);
		if (!identity) return byIdentity;
		const pair = byIdentity.get(identity) || { removed: [], added: [] };
		if (change.kind === 'SERIAL_REMOVED') pair.removed.push(change);
		if (change.kind === 'SERIAL_ADDED') pair.added.push(change);
		byIdentity.set(identity, pair);
		return byIdentity;
	}, new Map());
	const consumedIdentities = new Set();
	const groupedChanges = [];
	[...changesByIdentity.entries()]
		.sort(([left], [right]) => left.localeCompare(right))
		.forEach(([identity, pair]) => {
			if (
				pair.removed.length === 0 ||
				pair.removed.length !== pair.added.length
			)
				return;
			consumedIdentities.add(identity);
			const removed = [...pair.removed].sort(compareInventorySeries);
			const added = [...pair.added].sort(compareInventorySeries);
			removed.forEach((removedChange, index) => {
				const addedChange = added[index];
				const isSubstitution =
					removedChange.changeType === 'SERIAL_SUBSTITUTED';
				groupedChanges.push({
					kind: 'SERIAL_UPDATED',
					changeType: removedChange.changeType,
					series: isSubstitution ? null : removedChange.series,
					originalSeries: removedChange.series,
					targetSeries: addedChange.series,
					unit: addedChange.unit || removedChange.unit,
					original: removedChange.original,
					target: addedChange.target,
					delta: 0,
				});
			});
		});
	return changes
		.filter(change => !consumedIdentities.has(pairedInventoryChangeKey(change)))
		.concat(groupedChanges);
};

const getMaterialValidationThemeTokens = theme => ({
	background: theme._primaryBackgroundColor,
	surface: theme._secondaryBackgroundColor,
	elevatedSurface: theme._surfaceBackgroundColor,
	textPrimary: theme._highEmphasisColor,
	textSecondary: theme._mediumEmphasisColor,
	textInactive: theme._inactiveColor,
	border: theme._borderColor,
	accent: theme._secondaryColor,
	warning: theme._warningColor,
});

const Panel = styled.div`
	--material-validation-background: ${({ $tokens }) => $tokens.background};
	--material-validation-surface: ${({ $tokens }) => $tokens.surface};
	--material-validation-surface-elevated: ${({ $tokens }) =>
		$tokens.elevatedSurface};
	--material-validation-text: ${({ $tokens }) => $tokens.textPrimary};
	--material-validation-text-secondary: ${({ $tokens }) =>
		$tokens.textSecondary};
	--material-validation-text-inactive: ${({ $tokens }) => $tokens.textInactive};
	--material-validation-border: ${({ $tokens }) => $tokens.border};
	--material-validation-accent: ${({ $tokens }) => $tokens.accent};
	--material-validation-warning: ${({ $tokens }) => $tokens.warning};

	background: var(--material-validation-background);
	border-radius: 8px;
	box-sizing: border-box;
	color: var(--material-validation-text);
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-width: 0;
	padding: 12px;
	width: 100%;

	@media (max-width: 768px) {
		gap: 10px;
		padding: 10px;
	}

	@media (max-width: 390px) {
		border-radius: 6px;
		padding: 8px;
	}
`;

const Copy = styled.p`
	color: var(--material-validation-text-secondary);
	font-size: 12px;
	line-height: 1.45;
	margin: 0;
`;

const Summary = styled.section`
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-left: 3px solid var(--material-validation-accent);
	border-radius: 8px;
	display: grid;
	gap: 6px;
	padding: 10px 12px;
`;

const SummaryHeading = styled.h3`
	color: var(--material-validation-text);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const SummaryMeta = styled.div`
	color: var(--material-validation-text-secondary);
	display: flex;
	flex-wrap: wrap;
	font-size: 12px;
	gap: 4px 10px;
`;

const EvaluationOverview = styled.section`
	align-items: center;
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-left: 3px solid
		${({ $status }) =>
			$status === 'MATCH'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-warning)'};
	border-radius: 8px;
	display: grid;
	gap: 8px 12px;
	grid-template-columns: minmax(0, 1fr) auto;
	padding: 10px 12px;

	@media (max-width: 768px) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

const EvaluationCopy = styled.div`
	display: grid;
	gap: 2px;
	min-width: 0;
`;

const EvaluationHeading = styled.h3`
	color: var(--material-validation-text);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
`;

const EvaluationStatus = styled.span`
	color: var(--material-validation-text-secondary);
	font-size: 12px;
	font-weight: 700;
	line-height: 1.4;
`;

const EligibilityNotice = styled.section`
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-left: 3px solid var(--material-validation-warning);
	border-radius: 8px;
	color: var(--material-validation-text);
	font-size: 12px;
	line-height: 1.45;
	padding: 10px 12px;
`;

const EligibilityReasonList = styled.ul`
	color: var(--material-validation-text-secondary);
	margin: 6px 0 0;
	padding-left: 18px;

	li {
		overflow-wrap: break-word;
		overflow-wrap: anywhere;
	}
`;

const ManualCorrectionStatus = styled.section`
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-left: 3px solid var(--material-validation-accent);
	border-radius: 8px;
	color: var(--material-validation-text);
	font-size: 12px;
	line-height: 1.45;
	padding: 10px 12px;

	&:focus {
		outline: 2px solid var(--material-validation-accent);
		outline-offset: 2px;
	}
`;

const ComparisonList = styled.ol`
	display: grid;
	gap: 8px;
	list-style: none;
	margin: 0;
	padding: 0;
`;

const InventoryChanges = styled.section`
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-radius: 8px;
	display: grid;
	gap: 8px;
	padding: 10px 12px;
`;

const InventoryChangesHeading = styled.h3`
	color: var(--material-validation-text);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
`;

const InventoryChangesHeader = styled.div`
	align-items: center;
	display: flex;
	gap: 8px;
	justify-content: space-between;
	min-width: 0;

	@media (max-width: 768px) {
		align-items: stretch;
		flex-direction: column;
	}
`;

const InventoryViewControl = styled.div`
	background: var(--material-validation-surface-elevated);
	border: 1px solid var(--material-validation-border);
	border-radius: 7px;
	display: inline-flex;
	flex-shrink: 0;
	padding: 2px;

	@media (max-width: 768px) {
		align-self: stretch;
		width: 100%;
	}
`;

const InventoryViewButton = styled.button`
	appearance: none;
	background: ${({ $selected }) =>
		$selected
			? 'var(--material-validation-accent)'
			: 'var(--material-validation-surface-elevated)'};
	border: 0;
	border-radius: 5px;
	color: ${({ $selected }) =>
		$selected
			? 'var(--material-validation-background)'
			: 'var(--material-validation-text-secondary)'};
	cursor: pointer;
	font: inherit;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.35;
	min-height: 32px;
	padding: 6px 10px;

	&:hover {
		color: ${({ $selected }) =>
			$selected
				? 'var(--material-validation-background)'
				: 'var(--material-validation-text)'};
	}

	&:focus {
		outline: 2px solid var(--material-validation-accent);
		outline-offset: 2px;
	}

	@media (max-width: 768px) {
		flex: 1 1 50%;
	}

	@media (max-width: 390px) {
		min-width: 0;
	}
`;

const InventoryChangeList = styled.ul`
	display: grid;
	gap: 8px;
	list-style: none;
	margin: 0;
	padding: 0;
`;

const InventoryChangeItem = styled.li`
	background: var(--material-validation-surface-elevated);
	border-left: 3px solid var(--material-validation-accent);
	border-radius: 6px;
	display: grid;
	font-size: 12px;
	gap: 4px;
	min-width: 0;
	padding: 8px 10px;
`;

const InventoryChangeTitle = styled.strong`
	color: var(--material-validation-text);
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const InventoryChangeMaterial = styled.span`
	color: var(--material-validation-text);
	font-weight: 600;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const InventoryChangeMeta = styled.span`
	color: var(--material-validation-text-secondary);
	font-variant-numeric: tabular-nums;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const ComparisonItem = styled.li`
	background: var(--material-validation-surface);
	border: 1px solid var(--material-validation-border);
	border-left: 3px solid
		${({ $outcome }) =>
			$outcome === 'MATCH'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-warning)'};
	border-radius: 8px;
	display: grid;
	font-size: 12px;
	gap: 8px;
	min-width: 0;
	padding: 10px 12px;
`;

const ComparisonHeading = styled.h3`
	color: var(--material-validation-text);
	font-size: 13px;
	font-weight: 700;
	line-height: 1.4;
	margin: 0;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const ComparisonSubjectCode = styled.span`
	color: var(--material-validation-text-secondary);
	font-size: 11px;
	line-height: 1.35;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const ComparisonDetails = styled.dl`
	display: grid;
	gap: 8px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	margin: 0;

	@media (max-width: 768px) {
		grid-template-columns: minmax(0, 1fr);
	}

	@media (max-width: 390px) {
		gap: 6px;
	}
`;

const ComparisonMetric = styled.div`
	background: var(--material-validation-surface-elevated);
	border-radius: 6px;
	min-width: 0;
	padding: 8px;
`;

const ComparisonLabel = styled.dt`
	color: var(--material-validation-text-secondary);
	font-size: 11px;
	font-weight: 700;
	line-height: 1.35;
	margin: 0;
`;

const ComparisonValue = styled.dd`
	color: var(--material-validation-text);
	font-variant-numeric: tabular-nums;
	font-weight: 700;
	line-height: 1.4;
	margin: 4px 0 0;
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
`;

const OutcomeLabel = styled.span`
	align-items: center;
	border: 1px solid
		${({ $outcome }) =>
			$outcome === 'MATCH'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-warning)'};
	border-radius: 999px;
	color: var(--material-validation-text);
	display: inline-flex;
	font-size: 11px;
	font-weight: 700;
	justify-self: start;
	line-height: 1;
	padding: 5px 8px;
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	justify-content: flex-end;

	@media (max-width: 768px) {
		justify-content: flex-start;
	}

	@media (max-width: 390px) {
		flex-direction: column;

		button {
			width: 100%;
		}
	}
`;

const DecisionButton = styled(AntButton)`
	&& {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-surface)'};
		border-color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-border)'};
		border-radius: 8px;
		box-shadow: none;
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-validation-background)'
				: 'var(--material-validation-text)'};
		font-weight: 700;
		min-height: 40px;
		padding: 0 16px;
	}

	&&:hover {
		background: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-validation-accent)'
				: 'var(--material-validation-surface-elevated)'};
		border-color: var(--material-validation-accent);
		color: ${({ $intent }) =>
			$intent === 'primary'
				? 'var(--material-validation-background)'
				: 'var(--material-validation-accent)'};
		filter: brightness(1.08);
	}

	&&:focus {
		border-color: var(--material-validation-accent);
		outline: 2px solid var(--material-validation-accent);
		outline-offset: 2px;
	}

	&&[disabled] {
		background: var(--material-validation-surface-elevated);
		border-color: var(--material-validation-border);
		color: var(--material-validation-text-inactive);
		cursor: not-allowed;
		filter: none;
		opacity: 1;
	}

	&&.ant-btn-loading,
	&&.ant-btn-loading[disabled] {
		background: var(--material-validation-accent);
		border-color: var(--material-validation-accent);
		color: var(--material-validation-background);
		filter: none;
		opacity: 0.82;
	}
`;

function CurrentInventorySnapshot({ currentInventory }) {
	return (
		<>
			{currentInventory?.available === true ? (
				currentInventory.items.length > 0 ? (
					<InventoryChangeList aria-label='Inventario técnico actual'>
						{currentInventory.items.map((entry, index) => (
							<InventoryChangeItem
								key={`${entry.kind}-${entry.seriesId ?? entry.series ?? index}`}
							>
								<InventoryChangeTitle>
									{entry.kind === 'BULK'
										? 'Cantidad contabilizada'
										: `Serie actual · ${entry.series}`}
								</InventoryChangeTitle>
								<InventoryChangeMaterial>
									{entry.current.item.label}
								</InventoryChangeMaterial>
								<InventoryChangeMeta>
									Código: {entry.current.item.code}
								</InventoryChangeMeta>
								<InventoryChangeMeta>
									Cantidad:{' '}
									{formatMaterialValidationValue(
										entry.current.value,
										entry.unit,
									)}
								</InventoryChangeMeta>
								{entry.kind === 'SERIAL' && entry.current.state && (
									<InventoryChangeMeta>
										Estado: {entry.current.state.label}
									</InventoryChangeMeta>
								)}
							</InventoryChangeItem>
						))}
					</InventoryChangeList>
				) : (
					<Copy role='status'>
						No hay materiales registrados en el inventario actual.
					</Copy>
				)
			) : currentInventory?.available === false ? (
				<Copy role='status'>
					{CURRENT_INVENTORY_REASON_LABELS[currentInventory?.reason] ||
						'El inventario técnico actual no se puede verificar.'}
				</Copy>
			) : (
				<Copy role='status'>Estado actual no disponible en esta versión.</Copy>
			)}
		</>
	);
}

export default function MaterialValidationControl({
	viewModel,
	isLoading,
	loadError,
	contractError,
	decisionError,
	isSubmitting,
	manualRequestError,
	isRequestingManualCorrection,
	manualRefreshWarning,
	isRefreshingManualCorrection,
	isPollingManualAdjustment,
	isPolling,
	manualAdjustmentPollingExhausted,
	onRetry,
	onRefreshEvidence,
	onDecide,
	onRequestManualCorrection,
	onRetryManualCorrectionRefresh,
	onRefreshManualAdjustmentState,
}) {
	const { theme, themeProfile } = useTheme();
	const sendToast = useToastMessage();
	const themeTokens = getMaterialValidationThemeTokens(theme);
	const [mode, setMode] = useState();
	const [inventoryView, setInventoryView] = useState(INVENTORY_VIEW_AUDIT);
	const [shouldFocusManualStatus, setShouldFocusManualStatus] = useState(false);
	const modeRef = useRef(mode);
	const manualSubmitRef = useRef();
	const manualStatusRef = useRef();
	modeRef.current = mode;
	const materialCase = viewModel?.currentCase;
	const currentEvaluation = viewModel?.currentEvaluation;
	const requiresLiveEvaluation = requiresLiveMaterialEvaluation(
		materialCase,
		currentEvaluation,
	);
	const hasValidEvaluationHash = isValidMaterialEvaluationSha256(
		currentEvaluation?.contentSha256,
	);
	const consistencyState = viewModel?.consistency?.state;
	const decisions = viewModel?.capabilities?.decisions || [];
	const manualCorrectionRequest =
		viewModel?.capabilities?.manualCorrectionRequest;
	const canRequestManualCorrection = manualCorrectionRequest?.allowed === true;
	const manualCorrectionWaiting = isManualCorrectionWaiting(viewModel);
	const manualCorrectionReady = isManualCorrectionReadyForReview(viewModel);
	const isManualAdjustment = isManualCorrectionCase(materialCase);
	const manualDenialReasons =
		manualCorrectionRequest?.allowed === false
			? manualCorrectionRequest.reasons || []
			: [];
	const isBusy =
		isSubmitting ||
		isRequestingManualCorrection ||
		isLoading ||
		isPollingManualAdjustment ||
		isPolling;
	const canDecide =
		!!materialCase &&
		consistencyState === 'CONSISTENT' &&
		viewModel?.capabilities?.canDecide === true &&
		(!isManualAdjustment || manualCorrectionReady);
	const canApprove =
		canDecide &&
		decisions.includes('APPROVE') &&
		(!requiresLiveEvaluation || hasValidEvaluationHash);
	const canReturn =
		canDecide &&
		!manualCorrectionWaiting &&
		decisions.includes('RETURN_FOR_CORRECTION');

	useEffect(() => {
		if (modeRef.current === MANUAL_CORRECTION_MODE) return;
		setMode(undefined);
	}, [
		materialCase?.id,
		materialCase?.version,
		materialCase?.status,
		currentEvaluation?.contentSha256,
	]);

	useEffect(() => {
		setInventoryView(INVENTORY_VIEW_AUDIT);
	}, [viewModel?.orderId, materialCase?.id]);

	useEffect(() => {
		if (!shouldFocusManualStatus || !manualCorrectionWaiting || mode) return;
		manualStatusRef.current?.focus();
		setShouldFocusManualStatus(false);
	}, [manualCorrectionWaiting, mode, shouldFocusManualStatus]);

	const submitManualCorrection = payload => {
		if (manualSubmitRef.current) return manualSubmitRef.current;
		let request;
		try {
			request = onRequestManualCorrection(payload);
		} catch (error) {
			return Promise.reject(error);
		}
		const completion = Promise.resolve(request)
			.then(updated => {
				if (!updated) return updated;
				setMode(undefined);
				setShouldFocusManualStatus(true);
				sendToast({
					message:
						'La corrección de materiales quedó habilitada para el técnico.',
					type: 'success',
				});
				return updated;
			})
			.finally(() => {
				if (manualSubmitRef.current === completion)
					manualSubmitRef.current = undefined;
			});
		manualSubmitRef.current = completion;
		return completion;
	};

	if ((isLoading && !viewModel) || (!viewModel && !loadError && !contractError))
		return (
			<Panel
				aria-live='polite'
				aria-busy='true'
				data-theme={themeProfile}
				$tokens={themeTokens}
			>
				<Copy>Cargando validación de materiales…</Copy>
			</Panel>
		);

	if (loadError || contractError)
		return (
			<Panel aria-live='polite' data-theme={themeProfile} $tokens={themeTokens}>
				<strong>No se pudo confirmar la validación de materiales</strong>
				<Copy role='alert'>{contractError || loadError}</Copy>
				<Actions>
					<DecisionButton
						$intent='secondary'
						data-intent='secondary'
						onClick={onRetry}
						aria-label='Reintentar validación de materiales'
					>
						Reintentar
					</DecisionButton>
				</Actions>
			</Panel>
		);

	const comparisons = manualCorrectionWaiting
		? []
		: requiresLiveEvaluation
			? currentEvaluation?.comparisons || []
			: materialCase?.comparisons || [];
	const differences = manualCorrectionWaiting
		? undefined
		: comparisons.filter(comparison => comparison.outcome === 'MISMATCH')
				.length;
	const queuedCases = (viewModel.previousCases || []).filter(
		previousCase => previousCase.status === 'PENDING_REVIEW',
	);
	const pendingCount =
		(materialCase?.status === 'PENDING_REVIEW' ? 1 : 0) + queuedCases.length;
	const hasInventoryAuditContract = Object.prototype.hasOwnProperty.call(
		currentEvaluation || {},
		'inventoryAudit',
	);
	const inventoryAuditAvailable = hasInventoryAuditContract
		? currentEvaluation?.inventoryAudit?.available === true
			? true
			: currentEvaluation?.inventoryAudit?.available === false
				? false
				: undefined
		: undefined;
	const rawInventoryChanges = currentEvaluation?.inventoryChanges || [];
	const inventoryChanges =
		inventoryAuditAvailable === false
			? []
			: groupPairedInventoryChanges(rawInventoryChanges);
	const currentInventory = currentEvaluation?.currentInventory;
	const refreshEvidence = onRefreshEvidence || onRetry;
	const evaluationStatusLabel = currentEvaluation?.status
		? EVALUATION_STATUS_LABELS[currentEvaluation.status] ||
			'Estado no verificable'
		: 'Sin evaluación vigente';
	const isTechnicianInventoryValidation =
		materialCase?.resolutionKey === TECHNICIAN_INVENTORY_RESOLUTION_KEY;
	const isMatchingTechnicianEvaluation =
		isTechnicianInventoryValidation &&
		currentEvaluation?.status === 'MATCH' &&
		hasValidEvaluationHash;
	const isMatchingLiveCorrection =
		isMatchingTechnicianEvaluation && inventoryAuditAvailable === true;
	const isMatchingValidation =
		isMatchingTechnicianEvaluation && !isMatchingLiveCorrection;
	const showTechnicianAuditFallback =
		isTechnicianInventoryValidation &&
		inventoryChanges.length === 0 &&
		!manualCorrectionWaiting;
	const showInventoryChanges =
		inventoryChanges.length > 0 &&
		(!isManualAdjustment || manualCorrectionReady);
	const showInventorySection =
		showInventoryChanges || showTechnicianAuditFallback;
	const isCurrentInventoryView =
		isTechnicianInventoryValidation && inventoryView === INVENTORY_VIEW_CURRENT;
	const inventoryChangeLabel = change => {
		switch (change.kind) {
			case 'SERIAL_ADDED':
				return 'Serie agregada';
			case 'SERIAL_REMOVED':
				return 'Serie retirada';
			case 'SERIAL_RECATALOGED':
				return 'Serie recatalogada';
			case 'SERIAL_UPDATED':
				if (change.changeType === 'STATE_CHANGED') return 'Estado actualizado';
				if (change.changeType === 'SERIAL_SUBSTITUTED')
					return 'Serie sustituida';
				return 'Serie actualizada';
			default:
				return 'Cantidad contabilizada';
		}
	};
	const inventorySideValueLabel = (side, unit) => {
		if (!side) return 'No registrada';
		return formatMaterialValidationValue(side?.value, unit);
	};
	const hasMaterialIdentifier = value =>
		(typeof value === 'number' && Number.isFinite(value)) ||
		(typeof value === 'string' && value.trim().length > 0);
	const normalizeMaterialIdentifier = value => String(value).trim();
	const hasSameInventoryMaterialIdentity = (originalItem, targetItem) => {
		if (
			hasMaterialIdentifier(originalItem.itemId) &&
			hasMaterialIdentifier(targetItem.itemId)
		)
			return (
				normalizeMaterialIdentifier(originalItem.itemId) ===
				normalizeMaterialIdentifier(targetItem.itemId)
			);

		if (
			hasMaterialIdentifier(originalItem.code) &&
			hasMaterialIdentifier(targetItem.code)
		)
			return (
				normalizeMaterialIdentifier(originalItem.code) ===
				normalizeMaterialIdentifier(targetItem.code)
			);

		if (
			hasMaterialIdentifier(originalItem.label) &&
			hasMaterialIdentifier(targetItem.label)
		)
			return (
				normalizeMaterialIdentifier(originalItem.label) ===
				normalizeMaterialIdentifier(targetItem.label)
			);

		return false;
	};
	const shouldRenderInventoryMaterialTransition = change =>
		!!change.original?.item &&
		!!change.target?.item &&
		!hasSameInventoryMaterialIdentity(
			change.original.item,
			change.target.item,
		);
	const inventoryMaterialIdentityLabel = side => {
		const label = side?.item?.label || 'Material no identificado';
		const code = side?.item?.code;
		return code ? `${label} · Código: ${code}` : label;
	};
	const inventoryMaterialLabel = change =>
		change.target?.item?.label ||
		change.original?.item?.label ||
		'Material no identificado';
	const inventoryMaterialCode = change =>
		change.target?.item?.code || change.original?.item?.code;
	const inventoryStateLabel = side => {
		if (side?.state?.label) return side.state.label;
		if (side?.state?.id !== undefined && side?.state?.id !== null)
			return `ID ${side.state.id}`;
		return 'No registrado';
	};
	const inventoryDeltaLabel = change => {
		const value = formatMaterialValidationValue(change.delta, change.unit);
		return change.delta > 0 ? `+${value}` : value;
	};
	const orderCurrentInventory =
		!isTechnicianInventoryValidation && !isLoading
			? getOrderCurrentInventory(viewModel)
			: undefined;
	const showOrderCurrentInventory =
		orderCurrentInventory &&
		orderCurrentInventory.reason !== 'CURRENT_WTD_NOT_FOUND';

	return (
		<Panel
			aria-label='Caso vigente de validación de materiales'
			data-theme={themeProfile}
			$tokens={themeTokens}
		>
			{materialCase ? (
				<Summary aria-labelledby='material-validation-current-case-title'>
					<SummaryHeading id='material-validation-current-case-title'>
						{formatMaterialValidationCaseLabel(materialCase)}
					</SummaryHeading>
					<SummaryMeta>
						{differences !== undefined && (
							<span>
								{differences} {differences === 1 ? 'diferencia' : 'diferencias'}
							</span>
						)}
						{pendingCount > 0 && (
							<span>
								{pendingCount}{' '}
								{pendingCount === 1 ? 'caso pendiente' : 'casos pendientes'}
							</span>
						)}
					</SummaryMeta>
				</Summary>
			) : (
				<Copy>
					{canRequestManualCorrection
						? 'No existe un caso vigente. Puede habilitar una corrección excepcional para el técnico.'
						: viewModel.required === false
							? 'Esta orden no requiere validación de materiales.'
							: 'No existe un caso vigente para decidir.'}
				</Copy>
			)}

			{materialCase && (
				<EvaluationOverview
					aria-label='Estado actual de la validación de materiales'
					aria-busy={isLoading ? 'true' : undefined}
					$status={currentEvaluation?.status}
				>
					<EvaluationCopy>
						<EvaluationHeading>Validación actual</EvaluationHeading>
						<EvaluationStatus>
							Estado actual: {evaluationStatusLabel}
						</EvaluationStatus>
					</EvaluationCopy>
					{isTechnicianInventoryValidation && refreshEvidence && (
						<DecisionButton
							$intent='secondary'
							data-intent='secondary'
							disabled={isLoading}
							loading={isLoading}
							onClick={refreshEvidence}
							aria-label='Actualizar evidencia de validación de materiales'
						>
							Actualizar evidencia
						</DecisionButton>
					)}
				</EvaluationOverview>
			)}

			{comparisons.length > 0 && (
				<ComparisonList aria-label='Comparación de materiales reportados'>
					{comparisons.map(comparison => {
						const subject =
							comparison.subject?.label ||
							comparison.subject?.code ||
							comparison.metricKey;
						return (
							<ComparisonItem
								key={`${comparison.ordinal}-${comparison.metricKey}`}
								$outcome={comparison.outcome}
								data-outcome={comparison.outcome}
							>
								<ComparisonHeading>{subject}</ComparisonHeading>
								{comparison.subject?.code &&
									comparison.subject.code !== subject && (
										<ComparisonSubjectCode>
											Código: {comparison.subject.code}
										</ComparisonSubjectCode>
									)}
								<ComparisonDetails>
									<ComparisonMetric>
										<ComparisonLabel>
											{getComparisonExpectedLabel(comparison.operator)}:{' '}
										</ComparisonLabel>
										<ComparisonValue>
											{formatMaterialValidationValue(
												comparison.expectedValue,
												comparison.unit,
											)}
										</ComparisonValue>
									</ComparisonMetric>
									<ComparisonMetric>
										<ComparisonLabel>Reportado: </ComparisonLabel>
										<ComparisonValue>
											{formatMaterialValidationValue(
												comparison.reportedValue,
												comparison.unit,
											)}
										</ComparisonValue>
									</ComparisonMetric>
								</ComparisonDetails>
								<OutcomeLabel $outcome={comparison.outcome}>
									{getComparisonOutcomeLabel(comparison)}
								</OutcomeLabel>
							</ComparisonItem>
						);
					})}
				</ComparisonList>
			)}

			{manualCorrectionWaiting && (
				<ManualCorrectionStatus
					ref={manualStatusRef}
					role='status'
					aria-live='polite'
					aria-atomic='true'
					aria-busy={isPollingManualAdjustment ? 'true' : undefined}
					tabIndex='-1'
				>
					<strong>Corrección habilitada.</strong> El técnico está editando los
					materiales.
					{manualAdjustmentPollingExhausted && (
						<>
							<Copy>
								No detectamos una entrega todavía. Puede actualizar el estado
								manualmente.
							</Copy>
							<Actions>
								<DecisionButton
									$intent='secondary'
									data-intent='secondary'
									disabled={isPollingManualAdjustment}
									loading={isPollingManualAdjustment}
									onClick={onRefreshManualAdjustmentState}
									aria-label='Actualizar estado de la corrección manual'
								>
									Actualizar estado
								</DecisionButton>
							</Actions>
						</>
					)}
				</ManualCorrectionStatus>
			)}

			{manualCorrectionReady && (
				<ManualCorrectionStatus
					role='status'
					aria-live='polite'
					aria-atomic='true'
				>
					<strong>Corrección lista para revisión.</strong> Revise los cambios
					antes de decidir.
				</ManualCorrectionStatus>
			)}

			{showInventorySection && (
				<InventoryChanges
					aria-label={
						isCurrentInventoryView
							? 'Estado actual del inventario técnico'
							: !showInventoryChanges
								? 'Auditoría de cambios de inventario'
								: isTechnicianInventoryValidation
								? 'Cambios detectados en el inventario técnico'
								: 'Cambios de inventario que se aplicarán'
					}
					aria-live={
						!showInventoryChanges && !isCurrentInventoryView
							? 'polite'
							: undefined
					}
					role={
						!showInventoryChanges && !isCurrentInventoryView
							? 'status'
							: undefined
					}
				>
					<InventoryChangesHeader>
						<InventoryChangesHeading>
							{isCurrentInventoryView
								? 'Estado actual del inventario'
								: 'Auditoría de cambios de inventario'}
						</InventoryChangesHeading>
						{isTechnicianInventoryValidation && materialCase && (
							<InventoryViewControl
								role='group'
								aria-label='Vista del inventario técnico'
							>
								<InventoryViewButton
									type='button'
									$selected={!isCurrentInventoryView}
									aria-pressed={!isCurrentInventoryView}
									onClick={() => setInventoryView(INVENTORY_VIEW_AUDIT)}
								>
									Auditoría
								</InventoryViewButton>
								<InventoryViewButton
									type='button'
									$selected={isCurrentInventoryView}
									aria-pressed={isCurrentInventoryView}
									onClick={() => setInventoryView(INVENTORY_VIEW_CURRENT)}
								>
									Estado actual
								</InventoryViewButton>
							</InventoryViewControl>
						)}
					</InventoryChangesHeader>
					{isCurrentInventoryView ? (
						<>
							<Copy>
								Inventario técnico vigente que se utilizará al aprobar.
							</Copy>
							{isLoading ? (
								<Copy role='status'>Actualizando inventario…</Copy>
							) : (
								<CurrentInventorySnapshot currentInventory={currentInventory} />
							)}
						</>
					) : showInventoryChanges ? (
						<>
							<Copy>
								{isTechnicianInventoryValidation
									? 'Cambios reportados en la evaluación vigente del inventario técnico.'
									: 'Estos movimientos se aplicarán sin modificar el documento histórico ni los pagos cerrados.'}
							</Copy>
							<InventoryChangeList>
								{inventoryChanges.map((change, index) => (
									<InventoryChangeItem
										key={`${change.kind}-${change.series || index}`}
									>
										<InventoryChangeTitle>
											{inventoryChangeLabel(change)}
											{change.series ? ` · Serie: ${change.series}` : ''}
										</InventoryChangeTitle>
										{shouldRenderInventoryMaterialTransition(change) ? (
											<>
												<InventoryChangeMaterial>
													Material anterior:{' '}
													{inventoryMaterialIdentityLabel(change.original)}
												</InventoryChangeMaterial>
												<InventoryChangeMaterial>
													Material actual:{' '}
													{inventoryMaterialIdentityLabel(change.target)}
												</InventoryChangeMaterial>
											</>
										) : (
											<>
												<InventoryChangeMaterial>
													{inventoryMaterialLabel(change)}
												</InventoryChangeMaterial>
												{inventoryMaterialCode(change) && (
													<InventoryChangeMeta>
														Código: {inventoryMaterialCode(change)}
													</InventoryChangeMeta>
												)}
											</>
										)}
										{change.changeType === 'SERIAL_SUBSTITUTED' && (
											<>
												<InventoryChangeMeta>
													Serie anterior: {change.originalSeries}
												</InventoryChangeMeta>
												<InventoryChangeMeta>
													Serie actual: {change.targetSeries}
												</InventoryChangeMeta>
											</>
										)}
										<InventoryChangeMeta>
											Antes:{' '}
											{inventorySideValueLabel(change.original, change.unit)}
										</InventoryChangeMeta>
										<InventoryChangeMeta>
											Después:{' '}
											{inventorySideValueLabel(change.target, change.unit)}
										</InventoryChangeMeta>
										{(change.kind === 'SERIAL_UPDATED' ||
											change.original?.state ||
											change.target?.state) && (
											<>
												<InventoryChangeMeta>
													Estado anterior:{' '}
													{inventoryStateLabel(change.original)}
												</InventoryChangeMeta>
												<InventoryChangeMeta>
													Estado actual: {inventoryStateLabel(change.target)}
												</InventoryChangeMeta>
											</>
										)}
										<InventoryChangeMeta>
											Cambio: {inventoryDeltaLabel(change)}
										</InventoryChangeMeta>
									</InventoryChangeItem>
								))}
							</InventoryChangeList>
						</>
					) : (
						<Copy>
							{inventoryAuditAvailable === true
								? 'No se detectaron cambios de inventario entre la evidencia original y el inventario actual.'
								: inventoryAuditAvailable === false
									? INVENTORY_AUDIT_REASON_LABELS[
											currentEvaluation?.inventoryAudit?.reason
										] ||
										'La auditoría de cambios no está disponible para esta evaluación.'
									: 'Esta evaluación fue generada sin auditoría de cambios. Las comparaciones visibles se conservan sin inferir movimientos no reportados.'}
						</Copy>
					)}
				</InventoryChanges>
			)}

			{!isTechnicianInventoryValidation &&
				!Object.prototype.hasOwnProperty.call(viewModel, 'currentInventory') &&
				refreshEvidence && (
					<Actions>
						<DecisionButton
							$intent='secondary'
							data-intent='secondary'
							disabled={isLoading || isBusy}
							loading={isLoading}
							onClick={refreshEvidence}
							aria-label='Actualizar estado actual del inventario'
						>
							Actualizar estado actual
						</DecisionButton>
					</Actions>
				)}

			{showOrderCurrentInventory && (
				<InventoryChanges aria-label='Estado actual del inventario técnico'>
					<InventoryChangesHeader>
						<InventoryChangesHeading>
							Estado actual del inventario
						</InventoryChangesHeading>
						{refreshEvidence && (
							<DecisionButton
								$intent='secondary'
								data-intent='secondary'
								disabled={isLoading || isBusy}
								loading={isLoading}
								onClick={refreshEvidence}
								aria-label='Actualizar estado actual del inventario'
							>
								Actualizar estado
							</DecisionButton>
						)}
					</InventoryChangesHeader>
					{orderCurrentInventory.wtdStatus === 'OPEN' && (
						<Copy>
							<strong>Inventario pendiente del técnico</strong>
							<br />
							Todavía no aplicado al inventario físico
						</Copy>
					)}
					{orderCurrentInventory.wtdId && (
						<Copy>
							WTD #{orderCurrentInventory.wtdId} ·{' '}
							{orderCurrentInventory.wtdStatus === 'OPEN'
								? 'Abierto'
								: 'Cerrado'}
						</Copy>
					)}
					{orderCurrentInventory.capturedAt && (
						<Copy>
							Capturado:{' '}
							<time dateTime={orderCurrentInventory.capturedAt}>
								{new Date(orderCurrentInventory.capturedAt).toLocaleString()}
							</time>
						</Copy>
					)}
					{orderCurrentInventory.updatedAt && (
						<Copy>
							Última actualización del documento:{' '}
							<time dateTime={orderCurrentInventory.updatedAt}>
								{new Date(orderCurrentInventory.updatedAt).toLocaleString()}
							</time>
						</Copy>
					)}
					<CurrentInventorySnapshot currentInventory={orderCurrentInventory} />
				</InventoryChanges>
			)}

			{consistencyState === 'INCONSISTENT' && (
				<Copy $tone='warning'>
					El estado debe conciliarse antes de tomar una decisión.
				</Copy>
			)}
			{decisionError && (
				<Copy $tone='warning' role='alert'>
					{decisionError}
				</Copy>
			)}
			{manualRequestError &&
				manualDenialReasons.length === 0 &&
				mode !== MANUAL_CORRECTION_MODE && (
					<Copy $tone='warning' role='alert'>
						{manualRequestError}
					</Copy>
				)}
			{manualRefreshWarning && (
				<EligibilityNotice role='alert'>
					<strong>{manualRefreshWarning}</strong>
					<Actions>
						<DecisionButton
							$intent='secondary'
							data-intent='secondary'
							disabled={isRefreshingManualCorrection}
							loading={isRefreshingManualCorrection}
							onClick={onRetryManualCorrectionRefresh}
							aria-label='Reintentar actualización del estado de la corrección'
						>
							Reintentar actualización
						</DecisionButton>
					</Actions>
				</EligibilityNotice>
			)}

			{manualDenialReasons.length > 0 && (
				<EligibilityNotice
					aria-label='Motivos que impiden la corrección manual'
					role={manualRequestError ? 'alert' : undefined}
				>
					<strong>La corrección manual no está disponible.</strong>
					<EligibilityReasonList>
						{manualDenialReasons.map(reason => (
							<li key={reason.code}>{reason.message}</li>
						))}
					</EligibilityReasonList>
				</EligibilityNotice>
			)}

			<MaterialValidationHistory
				currentCase={materialCase}
				previousCases={viewModel.previousCases}
			/>

			{(canRequestManualCorrection || canReturn || canApprove) && (
				<Actions
					aria-label={
						canRequestManualCorrection
							? 'Acciones de corrección disponibles'
							: 'Decisiones disponibles'
					}
				>
					{canRequestManualCorrection && (
						<DecisionButton
							$intent='primary'
							data-intent='primary'
							type='primary'
							disabled={isBusy}
							loading={
								isRequestingManualCorrection && mode === MANUAL_CORRECTION_MODE
							}
							onClick={() => setMode(MANUAL_CORRECTION_MODE)}
						>
							Habilitar corrección de materiales
						</DecisionButton>
					)}
					{canReturn && (
						<DecisionButton
							$intent='secondary'
							data-intent='secondary'
							disabled={isBusy}
							loading={isSubmitting && mode === 'RETURN_FOR_CORRECTION'}
							onClick={() => setMode('RETURN_FOR_CORRECTION')}
						>
							Solicitar corrección
						</DecisionButton>
					)}
					{canApprove && (
						<DecisionButton
							$intent='primary'
							data-intent='primary'
							type='primary'
							disabled={isBusy}
							loading={isSubmitting && mode === 'APPROVE'}
							onClick={() => setMode('APPROVE')}
						>
							{isMatchingLiveCorrection
								? 'Confirmar corrección'
								: isMatchingValidation
									? 'Confirmar validación'
									: 'Aprobar excepción'}
						</DecisionButton>
					)}
				</Actions>
			)}

			<MaterialValidationDecisionModal
				mode={mode}
				materialCase={materialCase}
				isMatchingCorrection={isMatchingLiveCorrection}
				isMatchingValidation={isMatchingValidation}
				inventoryAuditAvailable={inventoryAuditAvailable}
				comparisons={comparisons}
				approvalQuantitySelection={
					viewModel?.capabilities?.approvalQuantitySelection
				}
				evidenceHash={
					currentEvaluation?.contentSha256 || materialCase?.input?.contentSha256
				}
				isSubmitting={isBusy}
				submissionError={
					mode === MANUAL_CORRECTION_MODE ? manualRequestError : decisionError
				}
				onCancel={() => setMode(undefined)}
				onSubmit={
					mode === MANUAL_CORRECTION_MODE ? submitManualCorrection : onDecide
				}
			/>
		</Panel>
	);
}
